import { useState, useEffect } from 'react';
import { Check, Scale, Timer, ThermometerSnowflake, HelpCircle } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { AnalyticalBalance } from '@/components/lab';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Using the Desiccator',
    content: 'Use crucible tongs - the crucible is extremely hot! The desiccator contains silica gel that absorbs moisture from the air. This prevents your hygroscopic product from absorbing water vapor, which would increase the mass.'
  },
  2: {
    title: 'Cooling Completely',
    content: 'Allow at least 10-15 minutes for cooling. Weighing a hot object creates convection currents that give unstable, inaccurate readings. A properly cooled crucible should not feel warm to the back of your hand (do not touch directly).'
  },
  3: {
    title: 'Weighing Final Product',
    content: 'Use the same analytical balance as before for consistency. Handle with tongs or tissue to avoid adding oils from your fingers. Record the mass to three decimal places (0.001 g).'
  },
  4: {
    title: 'Achieving Constant Mass',
    content: 'Two successive weighings within 0.01 g indicates heating is complete. If mass decreases significantly between weighings, more water or volatile matter was present. Repeat heating and weighing until constant.'
  }
};

export default function Part7FinalWeighing() {
  const [inDesiccator, setInDesiccator] = useState(false);
  const [coolingProgress, setCoolingProgress] = useState(0);
  const [cooled, setCooled] = useState(false);
  const [finalMass, setFinalMass] = useState<number | null>(null);
  const [massAttempts, setMassAttempts] = useState<number[]>([]);
  const [constantMassAchieved, setConstantMassAchieved] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // Simulated data from earlier parts
  const crucibleMass = 25.3456;
  const tinMass = 1.0123;

  // Cooling effect
  useEffect(() => {
    if (inDesiccator && coolingProgress < 100) {
      const timer = setInterval(() => {
        setCoolingProgress(prev => {
          const newProgress = Math.min(prev + 2, 100);
          if (newProgress >= 100) {
            setCooled(true);
          }
          return newProgress;
        });
      }, 50);
      return () => clearInterval(timer);
    }
  }, [inDesiccator, coolingProgress]);

  const handleTransferToDesiccator = () => {
    if (currentStep === 1) {
      setInDesiccator(true);
      setCurrentStep(2);
    }
  };

  const handleWeigh = () => {
    if (cooled && (currentStep === 3 || (currentStep === 4 && !constantMassAchieved))) {
      // Expected mass: crucible + tin oxide
      // SnO2: Sn(118.71) + 2*O(16) = 150.71 g/mol
      // Mass of SnO2 = tinMass * (150.71 / 118.71) ≈ tinMass * 1.2695
      const oxideMass = tinMass * 1.27;
      const baseMass = crucibleMass + oxideMass;
      const variation = massAttempts.length === 0 ? 0.015 : (Math.random() * 0.004);
      const newMass = baseMass + variation;

      setFinalMass(parseFloat(newMass.toFixed(3)));
      setMassAttempts(prev => [...prev, newMass]);

      if (currentStep === 3) {
        setCurrentStep(4);
      }
    }
  };

  // Auto advance to step 3 when cooled
  useEffect(() => {
    if (cooled && currentStep === 2) {
      setCurrentStep(3);
    }
  }, [cooled, currentStep]);

  const handleCheckConstantMass = () => {
    if (massAttempts.length >= 2) {
      const diff = Math.abs(massAttempts[massAttempts.length - 1] - massAttempts[massAttempts.length - 2]);
      if (diff <= 0.01) {
        setConstantMassAchieved(true);
      } else {
        // Need to reheat
        setCooled(false);
        setCoolingProgress(0);
        setCurrentStep(2);
      }
    } else if (massAttempts.length === 1) {
      // First weighing, need second
      setCooled(false);
      setCoolingProgress(0);
      setCurrentStep(2);
    }
  };

  const completedCount = [inDesiccator, cooled, finalMass !== null, constantMassAchieved].filter(Boolean).length;

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <Scale size={16} />
          <h3>Part VII: Final Weighing</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Transfer to Desiccator', hint: 'Use tongs - crucible is hot!', done: inDesiccator },
            { id: 2, title: 'Cool Completely', hint: 'At least 10 minutes', done: cooled },
            { id: 3, title: 'Weigh Final Product', hint: 'Record to 0.001 g', done: finalMass !== null },
            { id: 4, title: 'Check Constant Mass', hint: 'Within 0.01 g', done: constantMassAchieved }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {massAttempts.length > 0 && (
          <div className="mass-history">
            <h4>Mass Measurements</h4>
            {massAttempts.map((mass, i) => (
              <div key={i} className="mass-row">
                <span>Attempt {i + 1}:</span>
                <span className="value">{mass.toFixed(3)} g</span>
              </div>
            ))}
            {massAttempts.length >= 2 && (
              <div className="mass-diff">
                Δ = {Math.abs(massAttempts[massAttempts.length - 1] - massAttempts[massAttempts.length - 2]).toFixed(3)} g
              </div>
            )}
          </div>
        )}
      </div>

      <LabBench
        experimentName="Final Weighing"
        completedCount={completedCount}
        totalSteps={4}
        equipment={['Crucible tongs', 'Desiccator', 'Analytical balance']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="weighing-workspace">
          {/* Cooling Progress */}
          {inDesiccator && !cooled && (
            <div className="cooling-display">
              <ThermometerSnowflake size={20} />
              <span>Cooling in Desiccator</span>
              <div className="cooling-bar">
                <div className="cooling-fill" style={{ width: `${coolingProgress}%` }} />
              </div>
              <span>{coolingProgress}%</span>
            </div>
          )}

          <div className="equipment-row">
            {/* Desiccator */}
            <div
              className={`desiccator-container ${currentStep === 1 ? 'clickable' : ''}`}
              onClick={handleTransferToDesiccator}
            >
              <svg viewBox="0 0 120 120" width={100} height={100}>
                {/* Base */}
                <ellipse cx="60" cy="110" rx="50" ry="8" fill="#E8E8E8" />
                {/* Bottom section */}
                <path d="M15 90 L15 110 L105 110 L105 90" fill="#F5F5F5" stroke="#CCC" />
                {/* Silica gel */}
                <rect x="20" y="95" width="80" height="10" fill="#4169E1" rx="2" />
                {/* Glass dome */}
                <path
                  d="M15 90 Q15 35 60 28 Q105 35 105 90"
                  fill="rgba(200,230,255,0.3)"
                  stroke="#7A9BB8"
                  strokeWidth="2"
                />
                {/* Knob */}
                <circle cx="60" cy="28" r="8" fill="#888" />

                {/* Crucible inside */}
                {inDesiccator && (
                  <g>
                    <path
                      d="M45 70 L45 60 Q45 52 60 52 Q75 52 75 60 L75 70 Q75 76 60 76 Q45 76 45 70"
                      fill={cooled ? '#D3D3D3' : '#FFB366'}
                      stroke="#888"
                    />
                    {/* White product */}
                    <ellipse cx="60" cy="66" rx="12" ry="5" fill="#FFFEF5" />
                  </g>
                )}
              </svg>
              <span className="equipment-label">Desiccator</span>
            </div>

            {/* Analytical Balance */}
            <div
              className={`balance-container ${(cooled && currentStep === 3) || (currentStep === 4 && !constantMassAchieved) ? 'clickable' : ''}`}
              onClick={handleWeigh}
            >
              <AnalyticalBalance
                reading={finalMass || 0}
                stable={finalMass !== null}
                unit="g"
              />
              <span className="equipment-label">Analytical Balance</span>
            </div>
          </div>

          {/* Check Constant Mass Button */}
          {massAttempts.length >= 1 && currentStep === 4 && !constantMassAchieved && (
            <button className="check-btn" onClick={handleCheckConstantMass}>
              <Timer size={18} />
              {massAttempts.length < 2 ? 'Reheat & Reweigh' : 'Check Constant Mass'}
            </button>
          )}

          {/* Success Message */}
          {constantMassAchieved && (
            <div className="success-panel">
              <Check size={24} />
              <div>
                <h4>Constant Mass Achieved!</h4>
                <p>Final mass: <strong>{finalMass?.toFixed(3)} g</strong></p>
                <p className="formula">Mass of SnO₂ = {finalMass?.toFixed(3)} - {crucibleMass.toFixed(3)} = {(finalMass! - crucibleMass).toFixed(3)} g</p>
              </div>
            </div>
          )}

          <div className="info-box">
            <h4>Why Constant Mass?</h4>
            <p>
              If the mass changes by more than 0.01 g between weighings, the sample
              may still contain moisture or unreacted material. Repeat heating
              and weighing until mass is constant.
            </p>
          </div>
        </div>

        <style>{`
          .weighing-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .cooling-display {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 20px;
            background: rgba(59, 130, 246, 0.1);
            border-radius: 10px;
            color: #3B82F6;
          }

          .cooling-bar {
            width: 120px;
            height: 8px;
            background: var(--border);
            border-radius: 4px;
            overflow: hidden;
          }

          .cooling-fill {
            height: 100%;
            background: #3B82F6;
            transition: width 0.1s;
          }

          .equipment-row {
            display: flex;
            gap: 50px;
            align-items: flex-end;
          }

          .desiccator-container,
          .balance-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 12px;
            border-radius: 8px;
            transition: all 0.2s;
          }

          .clickable {
            cursor: pointer;
            animation: pulse 1.5s ease-in-out infinite;
          }

          .clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          @keyframes pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255, 90, 54, 0.4); }
            50% { box-shadow: 0 0 0 8px rgba(255, 90, 54, 0); }
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .mass-history {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 12px;
          }

          .mass-history h4 {
            margin: 0 0 8px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .mass-row {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            padding: 4px 0;
          }

          .mass-row .value {
            font-family: monospace;
            font-weight: 600;
          }

          .mass-diff {
            margin-top: 8px;
            padding: 6px;
            background: rgba(0, 168, 107, 0.1);
            border-radius: 4px;
            font-size: 11px;
            text-align: center;
            font-family: monospace;
          }

          .check-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 24px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .check-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .success-panel {
            display: flex;
            align-items: flex-start;
            gap: 16px;
            padding: 20px;
            background: rgba(0, 168, 107, 0.15);
            border: 2px solid var(--secondary);
            border-radius: 12px;
            color: var(--secondary);
          }

          .success-panel h4 {
            margin: 0 0 8px 0;
          }

          .success-panel p {
            margin: 4px 0;
            font-size: 13px;
          }

          .success-panel .formula {
            font-family: monospace;
            font-size: 12px;
            color: var(--text-primary);
          }

          .info-box {
            background: var(--bg-elevated);
            border-left: 3px solid var(--secondary);
            border-radius: 8px;
            padding: 16px;
            max-width: 400px;
          }

          .info-box h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--secondary);
          }

          .info-box p {
            margin: 0;
            font-size: 12px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp11"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
